import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { backendService } from '../services/BackendService';
import {
  SubscriptionStatus as APISubscriptionStatus,
  SubscriptionUsage as _APISubscriptionUsage,
} from '../api/subscriptions';
import { useAuth } from './useAuth';

// Define subscription plan types to match backend
export type SubscriptionPlan =
  | 'free'
  | 'plus'
  | 'freemium_trial'
  | 'pro'
  | 'pay_per_scan';

export interface SubscriptionStatus {
  subscription_plan: SubscriptionPlan;
  subscription_status: string;
  subscription_expires_at?: string;
  trial_started_at?: string;
  trial_expires_at?: string;
  is_premium: boolean;
  is_pro: boolean;
  is_plus: boolean;
  is_on_trial: boolean;
}

// Query keys
export const subscriptionKeys = {
  all: ['subscription'] as const,
  status: () => [...subscriptionKeys.all, 'status'] as const,
  usage: () => [...subscriptionKeys.all, 'usage'] as const,
};

// Storage key
const SUBSCRIPTION_STORAGE_KEY = 'coloursense_subscription';

// Helper function to determine if plan is premium
const isPremiumPlan = (planType: SubscriptionPlan): boolean => {
  return (
    planType === 'plus' ||
    planType === 'pro' ||
    planType === 'freemium_trial' ||
    planType === 'pay_per_scan'
  );
};

// Convert API response to internal format
const mapSubscriptionStatus = (
  apiStatus: APISubscriptionStatus
): SubscriptionStatus => ({
  subscription_plan: apiStatus.subscription_plan as SubscriptionPlan,
  subscription_status: apiStatus.subscription_status,
  subscription_expires_at: apiStatus.subscription_expires_at,
  trial_started_at: apiStatus.trial_started_at,
  trial_expires_at: apiStatus.trial_expires_at,
  is_premium: apiStatus.is_premium,
  is_pro: apiStatus.is_pro,
  is_plus: apiStatus.is_plus ?? false,
  is_on_trial: apiStatus.is_on_trial,
});

// Hook to get usage and remaining credits
export const useSubscriptionUsage = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: subscriptionKeys.usage(),
    queryFn: async () => {
      if (!isAuthenticated) return null;
      try {
        return await backendService.subscriptions.getUsage();
      } catch (err: unknown) {
        // 404 = backend may not have GET /subscriptions/usage yet (restart backend)
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 404) return null;
        throw err;
      }
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
    retry: 1,
  });
};

// Hook to get subscription status
export const useSubscriptionStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: subscriptionKeys.status(),
    queryFn: async () => {
      if (!isAuthenticated) {
        // Try to load from local storage
        try {
          const storedStatusString = await AsyncStorage.getItem(
            SUBSCRIPTION_STORAGE_KEY
          );
          if (storedStatusString) {
            return JSON.parse(storedStatusString) as SubscriptionStatus;
          }
        } catch (error) {
          console.error('Failed to load from local storage:', error);
        }
        return null;
      }

      try {
        const apiStatus = await backendService.subscriptions.getStatus();
        const status = mapSubscriptionStatus(apiStatus);

        // Store in local storage for offline access
        await AsyncStorage.setItem(
          SUBSCRIPTION_STORAGE_KEY,
          JSON.stringify(status)
        );

        return status;
      } catch (error) {
        console.warn(
          'Failed to load subscription from backend, falling back to local storage:',
          error
        );

        // Fallback to local storage
        try {
          const storedStatusString = await AsyncStorage.getItem(
            SUBSCRIPTION_STORAGE_KEY
          );
          if (storedStatusString) {
            return JSON.parse(storedStatusString) as SubscriptionStatus;
          }
        } catch (storageError) {
          console.error('Failed to load from local storage:', storageError);
        }
        return null;
      }
    },
    enabled: true, // Always enabled, but will return null if not authenticated
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
  });
};

// Main subscription hook
export const useSubscription = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { data: subscriptionStatus, isLoading: loadingSubscription } =
    useSubscriptionStatus();

  const plan = subscriptionStatus?.subscription_plan || 'free';
  const isPremium =
    isPremiumPlan(plan) && subscriptionStatus?.subscription_status === 'active';
  const isProUser =
    plan === 'pro' && subscriptionStatus?.subscription_status === 'active';
  const isPlusUser =
    plan === 'plus' && subscriptionStatus?.subscription_status === 'active';
  const isOnTrial =
    plan === 'freemium_trial' &&
    subscriptionStatus?.subscription_status === 'active';

  // Refresh subscription mutation
  const refreshSubscriptionMutation = useMutation({
    mutationFn: async () => {
      if (!isAuthenticated) {
        return null;
      }
      const apiStatus = await backendService.subscriptions.getStatus();
      const status = mapSubscriptionStatus(apiStatus);
      await AsyncStorage.setItem(
        SUBSCRIPTION_STORAGE_KEY,
        JSON.stringify(status)
      );
      return status;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(subscriptionKeys.status(), data);
    },
  });

  // Upgrade subscription mutation
  const upgradeSubscriptionMutation = useMutation({
    mutationFn: async () => {
      if (!isAuthenticated) {
        throw new Error('User must be authenticated to upgrade subscription');
      }
      await backendService.subscriptions.upgrade();
      const apiStatus = await backendService.subscriptions.getStatus();
      return mapSubscriptionStatus(apiStatus);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(subscriptionKeys.status(), data);
      AsyncStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(data));
    },
  });

  // Start freemium trial mutation
  const startFreemiumTrialMutation = useMutation({
    mutationFn: async () => {
      if (!isAuthenticated) {
        throw new Error('User must be authenticated to start freemium trial');
      }
      await backendService.subscriptions.startFreemiumTrial();
      const apiStatus = await backendService.subscriptions.getStatus();
      return mapSubscriptionStatus(apiStatus);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(subscriptionKeys.status(), data);
      AsyncStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(data));
    },
  });

  // Downgrade to free mutation
  const downgradeToFreeMutation = useMutation({
    mutationFn: async () => {
      if (!isAuthenticated) {
        throw new Error('User must be authenticated to downgrade subscription');
      }
      await backendService.subscriptions.downgrade();
      const apiStatus = await backendService.subscriptions.getStatus();
      return mapSubscriptionStatus(apiStatus);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(subscriptionKeys.status(), data);
      AsyncStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(data));
    },
  });

  // Restore purchases mutation
  const restorePurchasesMutation = useMutation({
    mutationFn: async () => {
      if (!isAuthenticated) {
        throw new Error('User must be authenticated to restore purchases');
      }
      await backendService.subscriptions.restore();
      const apiStatus = await backendService.subscriptions.getStatus();
      return mapSubscriptionStatus(apiStatus);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(subscriptionKeys.status(), data);
      AsyncStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(data));
    },
  });

  return {
    plan,
    isPremium,
    isProUser,
    isPlusUser,
    isOnTrial,
    subscriptionStatus,
    upgradeSubscription: upgradeSubscriptionMutation.mutateAsync,
    startFreemiumTrial: startFreemiumTrialMutation.mutateAsync,
    downgradeToFree: downgradeToFreeMutation.mutateAsync,
    restorePurchases: restorePurchasesMutation.mutateAsync,
    loadingSubscription,
    refreshSubscription: refreshSubscriptionMutation.mutateAsync,
  };
};
